import type { CreateForm, Form, FormKind, ResolvedForm, UpdateForm } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';

const formsPath = (projectId: string) => adminPath(`/projects/${projectId}/forms`);

export interface FormListFilters {
  kind?: FormKind;
  issueTypeId?: string;
}

/** `GET /admin/v1/projects/{id}/forms?kind=&issueTypeId=` (array). */
export function useForms(projectId: string | undefined, filters: FormListFilters = {}) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').forms({ ...filters }),
    queryFn: ({ signal }) =>
      request<Form[]>('GET', formsPath(projectId ?? ''), { query: { ...filters }, signal }),
    enabled: projectId !== undefined,
  });
}

export function useForm(projectId: string | undefined, formId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').form(formId ?? ''),
    queryFn: ({ signal }) =>
      request<Form>('GET', `${formsPath(projectId ?? '')}/${formId}`, { signal }),
    enabled: projectId !== undefined && formId !== undefined,
  });
}

/**
 * `GET .../forms/{fid}/resolved`: effective labels, required/read-only flags for the caller and
 * active options; fields the caller may not read are absent. What `SchemaForm` renders.
 */
export function useResolvedForm(projectId: string | undefined, formId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').resolvedForm(formId ?? ''),
    queryFn: ({ signal }) =>
      request<ResolvedForm>('GET', `${formsPath(projectId ?? '')}/${formId}/resolved`, { signal }),
    enabled: projectId !== undefined && formId !== undefined,
  });
}

export function useCreateForm(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateForm) => request<Form>('POST', formsPath(projectId), { body: input }),
    onSuccess: () => invalidate(keys.project(projectId).formsAll, keys.projects.detail(projectId)),
  });
}

export function useUpdateForm(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateForm }) =>
      request<Form>('PATCH', `${formsPath(projectId)}/${id}`, { body: patch }),
    onSuccess: () => invalidate(keys.project(projectId).formsAll, keys.projects.detail(projectId)),
  });
}

export function useDeleteForm(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${formsPath(projectId)}/${id}`),
    onSuccess: () => invalidate(keys.project(projectId).formsAll, keys.projects.detail(projectId)),
  });
}
